// Formazione personale — MOD 05 (T-251, T-531). La scheda usa lo stesso
// organigramma tenant_personnel del Manuale, può collegarlo agli account
// staff_profiles e conserva PDF e foto in un archivio append-only.

import {
  IonAlert, IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInput,
  IonItem, IonLabel, IonModal, IonNote, IonPage, IonSelect, IonSelectOption,
  IonSpinner, IonTitle, IonToast, IonToggle, IonToolbar,
} from '@ionic/react';
import {
  addOutline, checkmarkCircleOutline, cloudUploadOutline, documentTextOutline,
  openOutline, peopleOutline, trashOutline, warningOutline,
} from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { OperatorPinModal } from '../../components/OperatorPinModal.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  generateTenantPersonnelPin,
  shouldProvisionOperatorPin,
  type VisibleOperatorPin,
} from '../../lib/auth/sharedTabletOperatorApi.js';
import { canManagePersonnel } from '../../lib/auth/capabilities.js';
import { toUserMessage } from '../../lib/errors.js';
import { prepareFileUpload } from '../../lib/prepareFileUpload.js';
import { getSupabase } from '../../lib/supabase.js';
import {
  formatTrainingFileSize,
  removeTrainingCertificateAt,
  trainingCertificateExtension,
  trainingCertificateMime,
  validateTrainingCertificateFile,
} from './personnelTraining.js';

interface Attestato {
  id?: string;
  titolo?: string | null;
  ente?: string | null;
  data_rilascio?: string | null;
  data_scadenza?: string | null;
  file_storage_path?: string | null;
}

interface PersonRow {
  id: string;
  staff_profile_id: string | null;
  full_name: string;
  role: string;
  attivo: boolean;
  formazione_attestati: Attestato[];
}

interface StaffOption {
  id: string;
  display_name: string;
  kitchen_role: string;
}

interface FormState {
  id: string | null;
  staff_profile_id: string | null;
  full_name: string;
  role: string;
  attivo: boolean;
  attestati: Attestato[];
}

const EMPTY: FormState = {
  id: null,
  staff_profile_id: null,
  full_name: '',
  role: '',
  attivo: true,
  attestati: [],
};

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return y && m && dd ? `${dd}/${m}/${y}` : String(d);
}

function statoAttestato(scad: string | null | undefined): { label: string; color: string } {
  if (!scad) return { label: 'Senza scad.', color: '#64748b' };
  const days = Math.floor((new Date(scad + 'T00:00:00Z').getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: 'Scaduto', color: '#dc2626' };
  if (days <= 30) return { label: `In scadenza (${days}gg)`, color: '#c2410c' };
  return { label: 'Valido', color: '#15803d' };
}

export function FormazionePage(): JSX.Element {
  const { profile } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const tenantId = profile?.active_tenant_id ?? null;
  const canEdit = canManagePersonnel(profile);
  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const openedFromStaff = initialParams.get('from') === 'staff';
  const requestedNewPerson = initialParams.get('new') === '1';
  const [rows, setRows] = useState<PersonRow[]>([]);
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openingPath, setOpeningPath] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [visiblePin, setVisiblePin] = useState<VisibleOperatorPin | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!tenantId) {
      setRows([]);
      setStaff([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const sb = getSupabase();
    const [personnelResult, staffResult] = await Promise.all([
      sb.from('tenant_personnel')
        .select('id, staff_profile_id, full_name, role, attivo, formazione_attestati')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('full_name', { ascending: true }),
      sb.from('staff_profiles')
        .select('id, display_name, kitchen_role')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('display_name', { ascending: true }),
    ]);
    if (personnelResult.error) setToast('Errore personale: ' + personnelResult.error.message);
    if (staffResult.error) setToast('Errore staff: ' + staffResult.error.message);
    setRows((personnelResult.data ?? []).map((row) => {
      const person = row as PersonRow;
      return {
        ...person,
        formazione_attestati: Array.isArray(person.formazione_attestati)
          ? person.formazione_attestati
          : [],
      };
    }));
    setStaff((staffResult.data ?? []) as StaffOption[]);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!requestedNewPerson || !canEdit) return;
    setForm({ ...EMPTY, attestati: [] });
    setPendingFiles({});
    setOpen(true);
  }, [requestedNewPerson, canEdit]);

  const expiring = useMemo(() => rows.reduce((count, person) =>
    count + person.formazione_attestati.filter((attestato) => {
      const state = statoAttestato(attestato.data_scadenza);
      return state.label === 'Scaduto' || state.label.startsWith('In scadenza');
    }).length, 0), [rows]);

  const linkedStaffIds = useMemo(() => new Set(rows
    .filter((person) => person.id !== form.id && person.staff_profile_id)
    .map((person) => person.staff_profile_id as string)), [form.id, rows]);

  function openNew(): void {
    setForm({ ...EMPTY, attestati: [] });
    setPendingFiles({});
    setOpen(true);
  }

  function openEdit(person: PersonRow): void {
    setForm({
      id: person.id,
      staff_profile_id: person.staff_profile_id,
      full_name: person.full_name,
      role: person.role,
      attivo: person.attivo,
      attestati: person.formazione_attestati.map((attestato) => ({ ...attestato })),
    });
    setPendingFiles({});
    setOpen(true);
  }

  function selectStaff(staffProfileId: string): void {
    if (!staffProfileId) {
      setForm((current) => ({ ...current, staff_profile_id: null }));
      return;
    }
    const selected = staff.find((candidate) => candidate.id === staffProfileId);
    if (!selected) return;
    setForm((current) => ({
      ...current,
      staff_profile_id: selected.id,
      full_name: selected.display_name,
      role: selected.kitchen_role,
    }));
  }

  function addAttestato(): void {
    setForm((current) => ({
      ...current,
      attestati: [...current.attestati, {
        id: crypto.randomUUID(),
        titolo: '',
        ente: '',
        data_rilascio: '',
        data_scadenza: '',
      }],
    }));
  }

  function updateAttestato(index: number, key: keyof Attestato, value: string): void {
    setForm((current) => ({
      ...current,
      attestati: current.attestati.map((attestato, itemIndex) =>
        itemIndex === index ? { ...attestato, [key]: value } : attestato),
    }));
  }

  function removeAttestato(index: number): void {
    const attestato = form.attestati[index];
    if (attestato?.id) {
      setPendingFiles((current) => {
        const next = { ...current };
        delete next[attestato.id as string];
        return next;
      });
    }
    setForm((current) => ({
      ...current,
      attestati: removeTrainingCertificateAt(current.attestati, index),
    }));
    setRemoveIndex(null);
  }

  async function selectCertificate(index: number, file: File | undefined): Promise<void> {
    if (!file) return;
    const error = await validateTrainingCertificateFile(file);
    if (error) {
      setToast(error);
      return;
    }
    const attestatoId = form.attestati[index]?.id ?? crypto.randomUUID();
    setForm((current) => ({
      ...current,
      attestati: current.attestati.map((attestato, itemIndex) =>
        itemIndex === index ? { ...attestato, id: attestatoId } : attestato),
    }));
    setPendingFiles((current) => ({ ...current, [attestatoId]: file }));
  }

  async function openCertificate(storagePath: string): Promise<void> {
    setOpeningPath(storagePath);
    try {
      const { data, error } = await getSupabase().storage
        .from('manuals')
        .createSignedUrl(storagePath, 60 * 60);
      if (error || !data) throw error ?? new Error('signed_url_failed');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setToast(`Impossibile aprire il documento: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setOpeningPath(null);
    }
  }

  async function save(): Promise<void> {
    if (!tenantId) { setToast('Nessun ristorante attivo'); return; }
    if (!form.full_name.trim()) { setToast('Indica il nome'); return; }
    if (!form.role.trim()) { setToast('Indica il ruolo'); return; }

    const attestati = form.attestati
      .filter((attestato) => (attestato.titolo ?? '').trim().length > 0)
      .map((attestato) => ({
        ...(attestato.id ? { id: attestato.id } : {}),
        titolo: (attestato.titolo ?? '').trim(),
        ente: (attestato.ente ?? '').trim() || null,
        data_rilascio: attestato.data_rilascio || null,
        data_scadenza: attestato.data_scadenza || null,
        file_storage_path: attestato.file_storage_path ?? null,
      }));
    const savedIds = new Set(attestati.flatMap((attestato) => attestato.id ? [attestato.id] : []));
    if (Object.keys(pendingFiles).some((id) => !savedIds.has(id))) {
      setToast('Inserisci il titolo per ogni documento selezionato.');
      return;
    }

    const wasActive = form.id
      ? rows.find((person) => person.id === form.id)?.attivo ?? null
      : null;
    const provisionPin = shouldProvisionOperatorPin({ wasActive, isActive: form.attivo });

    setSaving(true);
    const sb = getSupabase();
    const payload = {
      tenant_id: tenantId,
      business_id: profile?.active_business_id ?? null,
      staff_profile_id: form.staff_profile_id,
      full_name: form.full_name.trim(),
      role: form.role.trim(),
      attivo: form.attivo,
      formazione_attestati: attestati,
    };
    const saveResult = form.id
      ? await sb.from('tenant_personnel').update({
        ...payload,
        updated_at: new Date().toISOString(),
      }).eq('id', form.id).select('id').single()
      : await sb.from('tenant_personnel').insert(payload).select('id').single();

    if (saveResult.error || !saveResult.data) {
      setSaving(false);
      setToast('Salvataggio non completato: ' + (saveResult.error?.message ?? 'risposta non valida'));
      return;
    }

    const personId = (saveResult.data as { id: string }).id;
    let pinResult: VisibleOperatorPin | null = null;
    let pinError: string | null = null;
    if (provisionPin) {
      try {
        pinResult = await generateTenantPersonnelPin(personId);
        await queryClient.invalidateQueries({ queryKey: ['shared_tablet_operators'] });
      } catch (error) {
        pinError = toUserMessage(error, 'Persona salvata, ma il PIN non è stato generato. Riprova dalla sezione Personale.');
      }
    }

    const uploadedPaths: Record<string, string> = {};
    const successfulIds = new Set<string>();
    try {
      for (const [attestatoId, file] of Object.entries(pendingFiles)) {
        const mimeType = trainingCertificateMime(file);
        if (!mimeType) throw new Error('Tipo di documento non supportato');
        const storagePath = `${tenantId}/training-certificates/${personId}/${crypto.randomUUID()}.${trainingCertificateExtension(mimeType)}`;
        const { bytes, sha256 } = await prepareFileUpload(file);
        const { error: uploadError } = await sb.storage.from('manuals').upload(storagePath, bytes, {
          contentType: mimeType,
          upsert: false,
          metadata: {
            source: 'personnel_training_certificate',
            original_filename: file.name,
            status: 'draft',
          },
        });
        if (uploadError) throw uploadError;

        const { error: registerError } = await sb.rpc('register_personnel_training_document_v2', {
          p_tenant_id: tenantId,
          p_tenant_personnel_id: personId,
          p_attestato_id: attestatoId,
          p_original_filename: file.name,
          p_storage_path: storagePath,
          p_sha256: sha256,
          p_mime_type: mimeType,
          p_file_size_bytes: bytes.byteLength,
        });
        if (registerError) {
          await sb.storage.from('manuals').remove([storagePath]);
          throw registerError;
        }
        uploadedPaths[attestatoId] = storagePath;
        successfulIds.add(attestatoId);
      }
    } catch (error) {
      setForm((current) => ({
        ...current,
        id: personId,
        attestati: current.attestati.map((attestato) => attestato.id && uploadedPaths[attestato.id]
          ? { ...attestato, file_storage_path: uploadedPaths[attestato.id] }
          : attestato),
      }));
      setPendingFiles((current) => Object.fromEntries(
        Object.entries(current).filter(([id]) => !successfulIds.has(id)),
      ));
      setSaving(false);
      if (pinResult) setVisiblePin(pinResult);
      const pinOutcome = pinResult && 'email_sent' in pinResult
        ? pinResult.email_sent ? 'PIN generato e inviato via email' : 'PIN generato; email non inviata'
        : pinError;
      setToast([
        `Persona salvata, ma un documento non è stato archiviato: ${error instanceof Error ? error.message : String(error)}`,
        pinOutcome,
      ].filter(Boolean).join(' · '));
      void load();
      return;
    }

    if (pinResult) setVisiblePin(pinResult);
    setSaving(false);
    const uploadedCount = successfulIds.size;
    const saveMessage = uploadedCount > 0
      ? `${form.id ? 'Personale aggiornato' : 'Personale aggiunto'} · ${uploadedCount} ${uploadedCount === 1 ? 'certificato archiviato' : 'certificati archiviati'}`
      : form.id ? 'Personale aggiornato' : 'Personale aggiunto';
    const pinMessage = pinResult && 'email_sent' in pinResult
      ? pinResult.email_sent
        ? 'PIN generato e inviato via email'
        : 'PIN generato; email non inviata'
      : pinError;
    setToast([saveMessage, pinMessage].filter(Boolean).join(' · '));
    setOpen(false);
    setForm({ ...EMPTY });
    setPendingFiles({});
    void load();
  }

  return (
    <IonPage>
      <IonHeader><IonToolbar>
        <IonButtons slot="start"><IonBackButton defaultHref={openedFromStaff ? '/settings/staff' : '/haccp/registri-compila'} /></IonButtons>
        <IonTitle>Formazione personale</IonTitle>
      </IonToolbar></IonHeader>
      <IonContent className="ion-padding">
        <p style={{ fontSize: 14, lineHeight: 1.5, color: '#475569', marginTop: 0 }}>
          Registro MOD 05: collega lo staff già presente nell’app oppure inserisci una persona
          senza account. Per ogni corso puoi archiviare il certificato ufficiale in PDF o come foto.
        </p>
        {expiring > 0 && (
          <IonItem color="warning" lines="none" style={{ borderRadius: 10, marginBottom: 8 }}>
            <IonIcon icon={warningOutline} slot="start" />
            <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>
              {expiring} {expiring === 1 ? 'attestato scaduto/in scadenza' : 'attestati scaduti/in scadenza'} — pianifica la formazione.
            </IonLabel>
          </IonItem>
        )}
        {!canEdit && (
          <IonNote style={{ display: 'block', marginBottom: 8 }}>
            Solo il ristoratore o il consulente HACCP può modificare l’organigramma e caricare certificati.
          </IonNote>
        )}
        {canEdit && (
          <IonButton expand="block" onClick={openNew} style={{ '--border-radius': '12px', marginBottom: 12 } as React.CSSProperties}>
            <IonIcon icon={addOutline} slot="start" /> Aggiungi o collega persona
          </IonButton>
        )}
        {loading ? <div style={{ textAlign: 'center', padding: 24 }}><IonSpinner /></div>
          : rows.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>Nessun personale registrato.</p>
          ) : rows.map((person) => (
            <div
              key={person.id}
              style={{
                background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8,
                boxShadow: '0 1px 2px rgba(15,23,42,.08)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <b style={{ fontSize: 15, color: '#0f172a' }}>{person.full_name}</b>
                  {person.staff_profile_id && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, fontSize: 12, color: '#1d4ed8' }}>
                      <IonIcon icon={peopleOutline} aria-hidden="true" /> Account app collegato
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ display: 'block', fontSize: 12, color: '#475569' }}>
                    {person.role}{person.attivo ? '' : ' · non attivo'}
                  </span>
                  {canEdit && (
                    <IonButton
                      size="small"
                      fill="clear"
                      onClick={() => openEdit(person)}
                      aria-label={`Modifica formazione di ${person.full_name}`}
                      style={{ minHeight: 44, margin: '0 -8px -8px 0' }}
                    >
                      Modifica
                    </IonButton>
                  )}
                </div>
              </div>
              {person.formazione_attestati.length === 0 ? (
                <div style={{ fontSize: 12, color: '#c2410c', marginTop: 6, fontWeight: 700 }}>Nessun attestato — da formare</div>
              ) : (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {person.formazione_attestati.map((attestato, index) => {
                    const state = statoAttestato(attestato.data_scadenza);
                    return (
                      <div key={attestato.id ?? index} style={{ borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12 }}>
                          <span style={{ color: '#334155' }}>
                            {attestato.titolo}{attestato.ente ? ` · ${attestato.ente}` : ''}
                            {attestato.data_scadenza ? ` · scad. ${fmtDate(attestato.data_scadenza)}` : ''}
                          </span>
                          <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: '#fff', background: state.color, padding: '2px 7px', borderRadius: 999 }}>
                            {state.label}
                          </span>
                        </div>
                        {attestato.file_storage_path && (
                          <IonButton
                            size="small"
                            fill="clear"
                            onClick={() => { void openCertificate(attestato.file_storage_path as string); }}
                            disabled={openingPath === attestato.file_storage_path}
                            aria-label={`Apri certificato ${attestato.titolo ?? ''}`}
                            style={{ minHeight: 44, margin: '2px 0 0 -8px' }}
                          >
                            <IonIcon icon={documentTextOutline} slot="start" />
                            {openingPath === attestato.file_storage_path ? 'Apertura…' : 'Apri certificato'}
                          </IonButton>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

        <IonModal isOpen={open} onDidDismiss={() => { setOpen(false); setRemoveIndex(null); }}>
          <IonHeader><IonToolbar>
            <IonTitle>{form.id ? 'Modifica persona' : 'Nuova persona'}</IonTitle>
            <IonButtons slot="end"><IonButton onClick={() => setOpen(false)}>Chiudi</IonButton></IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            <IonItem>
              <IonSelect
                label="Dipendente già presente nell’app"
                labelPlacement="stacked"
                interface="action-sheet"
                placeholder="Seleziona lo staff"
                value={form.staff_profile_id ?? ''}
                onIonChange={(event) => selectStaff(String(event.detail.value ?? ''))}
              >
                <IonSelectOption value="">Inserimento manuale (senza account)</IonSelectOption>
                {staff.map((option) => (
                  <IonSelectOption key={option.id} value={option.id} disabled={linkedStaffIds.has(option.id)}>
                    {option.display_name} · {option.kitchen_role}{linkedStaffIds.has(option.id) ? ' (già collegato)' : ''}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            <IonNote style={{ display: 'block', padding: '8px 16px 4px', lineHeight: 1.4 }}>
              La selezione compila nome e ruolo. Usa l’inserimento manuale per collaboratori che non accedono a TrackEAT.
            </IonNote>
            <IonItem><IonInput label="Nome e cognome *" labelPlacement="stacked" value={form.full_name} onIonInput={(event) => setForm((current) => ({ ...current, full_name: String(event.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Ruolo *" labelPlacement="stacked" placeholder="Es. Cuoco, Cameriere, Titolare" value={form.role} onIonInput={(event) => setForm((current) => ({ ...current, role: String(event.detail.value ?? '') }))} /></IonItem>
            <IonItem>
              <IonLabel className="ion-text-wrap">
                <strong>In servizio e visibile sul tablet staff</strong>
                <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0', lineHeight: 1.4 }}>
                  Al primo inserimento viene creato il PIN; se l’account è collegato, lo riceve automaticamente via email.
                </p>
              </IonLabel>
              <IonToggle
                checked={form.attivo}
                aria-label="In servizio e visibile sul tablet staff"
                onIonChange={(event) => setForm((current) => ({ ...current, attivo: event.detail.checked }))}
              />
            </IonItem>

            <div style={{ marginTop: 18, marginBottom: 8, fontSize: 13, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Attestati e certificati
            </div>
            {form.attestati.length === 0 && (
              <IonNote style={{ display: 'block', marginBottom: 8 }}>
                Nessun attestato. Aggiungi il corso e, se disponibile, il documento ufficiale in PDF o foto.
              </IonNote>
            )}
            {form.attestati.map((attestato, index) => {
              const pendingFile = attestato.id ? pendingFiles[attestato.id] : undefined;
              return (
                <section key={attestato.id ?? index} style={{ background: '#f8fafc', borderRadius: 12, padding: '10px', marginBottom: 12, border: '1px solid #cbd5e1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 44 }}>
                    <b style={{ fontSize: 13, color: '#334155' }}>Attestato {index + 1}</b>
                    <IonButton
                      size="small"
                      fill="clear"
                      color="danger"
                      onClick={() => setRemoveIndex(index)}
                      aria-label={`Rimuovi attestato ${index + 1}${attestato.titolo ? `: ${attestato.titolo}` : ''}`}
                      style={{ minHeight: 44, margin: 0 }}
                    >
                      <IonIcon icon={trashOutline} slot="start" /> Rimuovi
                    </IonButton>
                  </div>
                  <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}><IonInput label="Titolo *" labelPlacement="stacked" placeholder="Es. Attestato HACCP" value={attestato.titolo ?? ''} onIonInput={(event) => updateAttestato(index, 'titolo', String(event.detail.value ?? ''))} /></IonItem>
                  <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}><IonInput label="Ente" labelPlacement="stacked" value={attestato.ente ?? ''} onIonInput={(event) => updateAttestato(index, 'ente', String(event.detail.value ?? ''))} /></IonItem>
                  <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}><IonInput type="date" label="Rilascio" labelPlacement="stacked" value={attestato.data_rilascio ?? ''} onIonInput={(event) => updateAttestato(index, 'data_rilascio', String(event.detail.value ?? ''))} /></IonItem>
                  <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}><IonInput type="date" label="Scadenza" labelPlacement="stacked" value={attestato.data_scadenza ?? ''} onIonInput={(event) => updateAttestato(index, 'data_scadenza', String(event.detail.value ?? ''))} /></IonItem>

                  <div style={{ borderTop: '1px solid #cbd5e1', marginTop: 8, paddingTop: 12 }}>
                    {attestato.file_storage_path && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#166534', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                        <IonIcon icon={checkmarkCircleOutline} aria-hidden="true" /> Documento archiviato
                      </div>
                    )}
                    {attestato.file_storage_path && (
                      <IonButton
                        expand="block"
                        fill="outline"
                        onClick={() => { void openCertificate(attestato.file_storage_path as string); }}
                        disabled={openingPath === attestato.file_storage_path}
                        style={{ minHeight: 44, '--border-radius': '10px' } as React.CSSProperties}
                      >
                        <IonIcon icon={openOutline} slot="start" /> Apri documento ufficiale
                      </IonButton>
                    )}
                    <label
                      style={{
                        minHeight: 44, border: '1px solid #2563eb', borderRadius: 10, color: '#1d4ed8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        padding: '8px 12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 8,
                      }}
                    >
                      <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
                      {attestato.file_storage_path ? 'Carica nuova foto o PDF' : 'Scatta o allega attestato'}
                      <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                        capture="environment"
                        aria-label={`Scatta o seleziona il certificato per l'attestato ${index + 1}`}
                        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)' }}
                        onChange={(event) => { void selectCertificate(index, event.target.files?.[0]); event.target.value = ''; }}
                      />
                    </label>
                    {pendingFile && (
                      <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: '#dbeafe', color: '#1e3a8a', fontSize: 13, lineHeight: 1.4 }}>
                        <strong>Pronto per il caricamento:</strong> {pendingFile.name} ({formatTrainingFileSize(pendingFile.size)})
                        <IonButton
                          size="small"
                          fill="clear"
                          onClick={() => setPendingFiles((current) => {
                            const next = { ...current };
                            if (attestato.id) delete next[attestato.id];
                            return next;
                          })}
                          style={{ minHeight: 44, margin: '4px 0 -6px -8px' }}
                        >
                          Annulla selezione
                        </IonButton>
                      </div>
                    )}
                    <IonNote style={{ display: 'block', marginTop: 8, lineHeight: 1.4 }}>
                      PDF o foto (JPG, PNG, HEIC, WebP) massimo 20 MB. Sul telefono puoi usare la fotocamera; l’archivio prova la presenza del file, non certifica firma o validità del contenuto.
                    </IonNote>
                  </div>
                </section>
              );
            })}
            <IonButton expand="block" fill="outline" onClick={addAttestato} style={{ minHeight: 44, '--border-radius': '10px' } as React.CSSProperties}>
              <IonIcon icon={addOutline} slot="start" /> Aggiungi attestato
            </IonButton>

            <IonButton expand="block" disabled={saving} onClick={() => { void save(); }} style={{ minHeight: 48, marginTop: 18, '--border-radius': '12px' } as React.CSSProperties}>
              {saving ? <><IonSpinner name="dots" />&nbsp; Salvataggio e archivio…</> : 'Salva'}
            </IonButton>
          </IonContent>
        </IonModal>
        <IonAlert
          isOpen={removeIndex !== null}
          header="Rimuovere l’attestato?"
          message={removeIndex !== null && form.attestati[removeIndex]?.file_storage_path
            ? 'L’attestato non comparirà più nella scheda dopo il salvataggio. Il documento ufficiale e la registrazione storica resteranno conservati nell’archivio HACCP.'
            : 'L’attestato non comparirà più nella scheda dopo il salvataggio.'}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Rimuovi',
              role: 'destructive',
              handler: () => { if (removeIndex !== null) removeAttestato(removeIndex); },
            },
          ]}
          onDidDismiss={() => setRemoveIndex(null)}
        />
        <OperatorPinModal value={visiblePin} onDismiss={() => setVisiblePin(null)} />
        <IonToast isOpen={!!toast} message={toast ?? ''} duration={3200} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
